<template>
	<button
		:name="name"
		:aria-label="name"
		class="app-button button-wrap"
		:class="[
			sizeClass,
			{ rounded: rounded },
			{ disabled: disabled },
			{ outline: outline || link },
			{ loading: loading },
			{ pill: pill },
			{ 'hide-outline': link },
			`button-color-${color}`,
			{ 'fit-content': fitContent },
		]"
		:disabled="disabled"
		@click="handleClick"
	>
		<CustomHollowDotsSpinner v-if="loading" :animation-duration="1000" :dot-size="dotSize" :color="loaderColor" class="loader" />
		<div class="button-content" :class="[contentSizeClass, { hide: loading }]">
			<div v-if="icon && !rightIcon" class="button-icon mr-2">
				<InlineSvg class="icon-svg" :src="icon" />
			</div>
			<slot class="no-wrap"></slot>
			<div v-if="icon && rightIcon" class="button-icon ml-2">
				<InlineSvg class="icon-svg" :src="icon" />
			</div>
		</div>
	</button>
</template>
<script>
	import { mapGetters } from 'vuex'
	import CustomHollowDotsSpinner from '~/stan-vue-shared/components/HollowDotsSpinner'

	export default {
		name: 'AppButton',
		components: { CustomHollowDotsSpinner },
		props: {
			name: { type: String, required: true }, // For Analytics Purpose
			disabled: { type: Boolean, default: false },
			outline: { type: Boolean, default: false },
			link: { type: Boolean, default: false },
			rounded: { type: Boolean, default: false },
			size: { type: String, default: 'md' }, // sm/small, md/medium, lg/large, xl
			loading: { type: Boolean, default: false },
			icon: { type: String, default: '' },
			pill: { type: Boolean, default: false },
			color: {
				type: String,
				default: 'primary',
				validator: value => ['primary', 'dark', 'danger', 'gray', 'light', 'black'].includes(value),
			},
			fitContent: { type: Boolean, default: false },
			rightIcon: { type: Boolean, default: false },
		},
		data() {
			return {
				contentWidth: 10,
				sizeMapping: {
					default: {
						dotSize: 10,
						sizeClass: 'button-md',
						contentSizeClass: 'h5 text-bold',
					},
					xs: {
						dotSize: 5,
						sizeClass: 'button-xs',
						contentSizeClass: 'para-3 text-bold',
					},
					xtrasmall: {
						dotSize: 5,
						sizeClass: 'button-xs',
						contentSizeClass: 'para-3 text-bold',
					},
					sm: {
						dotSize: 7,
						sizeClass: 'button-sm',
						contentSizeClass: 'para-3 text-bold',
					},
					small: {
						dotSize: 7,
						sizeClass: 'button-sm',
						contentSizeClass: 'para-3 text-bold',
					},
					md: {
						dotSize: 10,
						sizeClass: 'button-md',
						contentSizeClass: 'h5 text-bold',
					},
					medium: {
						dotSize: 10,
						sizeClass: 'button-md',
						contentSizeClass: 'h5 text-bold',
					},
					lg: {
						dotSize: 12,
						sizeClass: 'button-lg',
						contentSizeClass: 'h4 text-bold',
					},
					large: {
						dotSize: 12,
						sizeClass: 'button-lg',
						contentSizeClass: 'h4 text-bold',
					},
					xl: {
						dotSize: 14,
						sizeClass: 'button-xl',
						contentSizeClass: 'h3 text-bold',
					},
				},
			}
		},
		computed: {
			...mapGetters('Auth', ['user']),
			loaderColor() {
				if (!this.outline && !this.link) {
					return 'var(--stan-white)'
				}
				switch (this.color) {
					case 'danger':
						return 'var(--stan-negative-primary-color)'
					case 'dark':
						return 'var(--stan-primary-dark-color)'
					case 'light':
						return 'var(--stan-text-light-color)'
					case 'gray':
						return 'var(--stan-text-light-color)'
					case 'black':
						return 'var(--stan-black)'
					default:
						return 'var(--stan-primary-primary-color)'
				}
			},
			dotSize() {
				return this.sizeMapping[this.size]?.dotSize ?? this.sizeMapping.default.dotSize
			},
			sizeClass() {
				return this.sizeMapping[this.size]?.sizeClass ?? this.sizeMapping.default.sizeClass
			},
			contentSizeClass() {
				return this.sizeMapping[this.size]?.contentSizeClass ?? this.sizeMapping.default.contentSizeClass
			},
		},
		mounted() {},
		methods: {
			handleClick() {
				if (!this.disabled && !this.loading) {
					if (this.name) {
						this.$stanAnalytics('admin-button-click', {
							meta: { user_id: this.user?.user_id, username: this.user?.username },
							props: {
								button_name: this.name,
							},
						})
					}

					this.$emit('click')
				}
			},
		},
	}
</script>
<style lang="scss" scoped>
	@mixin outlineButton($mainColor, $disabledTextColor, $disabledColor, $hoverColor, $activeColor, $focusColor) {
		background-color: transparent;
		color: $mainColor;
		border-color: $mainColor;

		.button-content {
			.button-icon {
				::v-deep {
					.icon-svg {
						path {
							fill: $mainColor !important;
						}
					}
				}
			}
		}

		// Note: Order is important since the `hover` event overlaps with `click` so higher priority should be lower down as it overrides the class above
		&:hover,
		&.hover {
			background-color: $hoverColor;
		}

		&:active,
		&.active {
			background-color: $activeColor;
		}

		&:focus-visible,
		&.focus-visible {
			outline: none;
			box-shadow: 0px 0px 0px 4px $focusColor;
		}

		&.disabled {
			pointer-events: none;
			color: $disabledTextColor;
			border-color: $disabledColor;
			background-color: $disabledColor;
			.button-content {
				.button-icon {
					::v-deep {
						.icon-svg {
							path {
								fill: $disabledTextColor !important;
							}
						}
					}
				}
			}
		}

		.button-content {
			.button-icon {
				::v-deep {
					.icon-svg {
						path {
							fill: $mainColor;
						}
					}
				}
			}
		}

		&.hide-outline {
			border: none;
			.button-content {
				line-height: 1.125rem;
			}

			&:hover,
			&.hover {
				background: transparent;
			}

			&:active,
			&.active {
				background: transparent;
			}

			&:focus-visible,
			&.focus-visible {
				outline: none;
				box-shadow: 0px 0px 0px 4px $focusColor;
			}

			&.disabled {
				pointer-events: none;
				color: $disabledTextColor;
				background: transparent;
				.button-content {
					.button-icon {
						::v-deep {
							.icon-svg {
								path {
									fill: $disabledTextColor !important;
								}
							}
						}
					}
				}
			}
		}
	}

	@mixin primaryButton($loadingColor, $disabledTextColor, $disabledColor, $hoverColor, $activeColor, $focusColor) {
		&.loading {
			pointer-events: none;
			border-color: $loadingColor;
			background-color: $loadingColor;
		}

		// Note: Order is important since the `hover` event overlaps with `click` so higher priority should be lower down as it overrides the class above
		&:hover,
		&.hover {
			border-color: $hoverColor;
			background-color: $hoverColor;
		}

		&:active,
		&.active {
			background-color: $activeColor;
		}

		&:focus-visible,
		&.focus-visible {
			outline: none;
			box-shadow: 0px 0px 0px 4px $focusColor;
		}

		&.disabled {
			pointer-events: none;
			color: $disabledTextColor;
			border-color: $disabledColor;
			background-color: $disabledColor;
			.button-content {
				.button-icon {
					::v-deep {
						.icon-svg {
							path {
								fill: $disabledTextColor !important;
							}
						}
					}
				}
			}
		}
	}

	.button-wrap {
		position: relative;
		overflow: hidden;
		display: flex;
		align-items: center;
		justify-content: center;
		border-radius: 8px;
		border: 1px solid var(--stan-primary-primary-color);
		background-color: var(--stan-primary-primary-color);
		color: var(--stan-white);
		cursor: pointer;
		transition: background-color 150ms ease, color 150ms ease, border-color 150ms ease, box-shadow 150ms ease;
		// The focus visible is a dropshadow so leave some space to ensure the button sizing accounts for this
		margin: 5px;
		&.w-100 {
			// Allow button to work with w-100 while allowing for margin
			width: calc(100% - 10px) !important;
		}

		&.pill {
			border-radius: 50px;
		}

		.button-content {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 100%;
			line-height: 1em;

			&.hide {
				opacity: 0%;
			}

			.button-icon {
				transform: scale(0.875);
				aspect-ratio: 1;

				::v-deep {
					.icon-svg {
						path {
							fill: white;
						}
					}
				}
			}
		}

		@include primaryButton(
			var(--stan-button-primary-color),
			var(--stan-button-primary-disabled-text-color),
			var(--stan-button-primary-disabled-color),
			var(--stan-button-primary-hover-color),
			var(--stan-button-primary-active-color),
			var(--stan-button-primary-focus-color)
		);

		&.outline {
			@include outlineButton(
				var(--stan-primary-primary-color),
				var(--stan-gray-dark-color-ada),
				var(--stan-gray-strike-color),
				var(--stan-primary-light-color),
				var(--stan-primary-button-outline-pressed-color),
				var(--stan-primary-soft-color)
			);
		}

		&.button-sm {
			min-width: 100px;
			height: 32px;
			padding: 8px 11px 8px 10px;
			gap: 5px;
			&.rounded {
				border-radius: 16px !important;
			}
			.button-icon {
				::v-deep {
					.icon-svg {
						height: 16px;
						width: 16px;
					}
				}
			}
		}

		&.button-xs {
			height: 32px;
			padding: 8px 11px 8px 10px;
			gap: 5px;
			&.rounded {
				border-radius: 8px !important;
			}
			.button-icon {
				::v-deep {
					.icon-svg {
						height: 15px;
						width: 15px;
					}
				}
			}
		}

		&.button-md {
			min-width: 133px;
			height: 42px;
			padding: 15px;
			gap: 5px;
			&.rounded {
				border-radius: 21px !important;
			}
			.button-icon {
				::v-deep {
					.icon-svg {
						height: 24px;
						width: 24px;
					}
				}
			}
		}

		&.button-lg {
			min-width: 135px;
			height: 50px;
			padding: 15px 20px 15px 18px;
			gap: 8px;
			&.rounded {
				border-radius: 25px !important;
			}
			.button-icon {
				height: 24px;
				width: 24px;
				::v-deep {
					.icon-svg {
						height: 24px;
						width: 24px;
					}
				}
			}
		}

		&.button-xl {
			min-width: 183px;
			height: 60px;
			padding: 20px 25px;
			gap: 5px;
			&.rounded {
				border-radius: 30px !important;
			}
			.button-icon {
				::v-deep {
					.icon-svg {
						height: 28px;
						width: 28px;
					}
				}
			}
		}

		&.fit-content {
			width: fit-content;
			min-width: unset;
		}

		&.button-color-danger {
			background-color: var(--stan-negative-primary-color);
			border-color: var(--stan-negative-primary-color);
			color: #fff;

			.button-content {
				.button-icon {
					::v-deep {
						.icon-svg {
							path {
								fill: #fff !important;
							}
						}
					}
				}
			}

			@include primaryButton(
				var(--stan-negative-strike-color),
				#fff,
				var(--stan-negative-soft-color),
				var(--stan-negative-strike-color),
				var(--stan-negative-strike-color),
				var(--stan-negative-strike-color)
			);

			&.outline {
				@include outlineButton(
					var(--stan-negative-primary-color),
					var(--stan-gray-dark-color-ada),
					var(--stan-gray-strike-color),
					var(--stan-negative-light-color),
					var(--stan-negative-light-color),
					var(--stan-negative-light-color)
				);
			}
		}

		&.button-color-black {
			background-color: var(--stan-black);
			border-color: var(--stan-black);
			color: #fff;

			.button-content {
				.button-icon {
					::v-deep {
						.icon-svg {
							path {
								fill: #fff !important;
							}
						}
					}
				}
			}

			@include primaryButton(var(--stan-black), #fff, var(--stan-gray-dark-color), #1a1a1a, #333, var(--stan-primary-soft-color));

			&.outline {
				@include outlineButton(
					var(--stan-black),
					var(--stan-black),
					var(--stan-gray-dark-color),
					#1a1a1a80,
					#33333380,
					var(--stan-primary-soft-color)
				);
			}
		}

		&.button-color-dark {
			background-color: var(--stan-primary-dark-color);
			border-color: var(--stan-primary-dark-color);
			color: #fff;

			.button-content {
				.button-icon {
					::v-deep {
						.icon-svg {
							path {
								fill: #fff !important;
							}
						}
					}
				}
			}

			@include primaryButton(
				var(--stan-primary-strike-color),
				#fff,
				var(--stan-primary-soft-color),
				var(--stan-primary-strike-color),
				var(--stan-primary-strike-color),
				var(--stan-primary-strike-color)
			);

			&.outline {
				@include outlineButton(
					var(--stan-primary-dark-color),
					var(--stan-gray-dark-color-ada),
					var(--stan-gray-strike-color),
					var(--stan-primary-light-color),
					var(--stan-primary-light-color),
					var(--stan-primary-light-color)
				);
			}
		}
		&.button-color-light {
			background-color: var(--stan-text-light-color);
			border-color: var(--stan-text-light-color);
			color: #fff;

			.button-content {
				.button-icon {
					::v-deep {
						.icon-svg {
							path {
								fill: #fff !important;
							}
						}
					}
				}
			}

			@include primaryButton(
				var(--stan-primary-light-color),
				#fff,
				var(--stan-primary-soft-color),
				var(--stan-primary-strike-color),
				var(--stan-primary-strike-color),
				var(--stan-primary-light-color)
			);

			&.outline {
				@include outlineButton(
					var(--stan-text-light-color),
					var(--stan-gray-dark-color-ada),
					var(--stan-gray-primary-color),
					var(--stan-primary-light-color),
					var(--stan-primary-light-color),
					var(--stan-primary-light-color)
				);
			}
		}
		.loader {
			position: absolute;
		}

		&.button-color-gray {
			background-color: var(--stan-text-light-color);
			border-color: var(--stan-text-light-color);
			color: #fff;

			.button-content {
				.button-icon {
					::v-deep {
						.icon-svg {
							path {
								fill: #fff !important;
							}
						}
					}
				}
			}

			@include primaryButton(
				var(--stan-text-light-color),
				var(--stan-gray-dark-color-ada),
				var(--stan-gray-dark-color),
				var(--stan-gray-dark-color-ada),
				var(--stan-text-light-color),
				var(--stan-text-light-color)
			);

			&.outline {
				@include outlineButton(
					var(--stan-text-light-color),
					var(--stan-gray-dark-color-ada),
					var(--stan-gray-primary-color),
					var(--stan-gray-primary-color),
					var(--stan-gray-primary-color),
					var(--stan-gray-primary-color)
				);
			}
		}
	}
</style>
